import { Location, Route } from 'vue-router';

// Global URL parameters (e.g. ?anr=ADR8861 when opened from Consolidate) that are passed to
// every data model query. They are kept in sessionStorage so they survive the login redirect
// and navigation to other dashboards.
const ANR_KEY = 'dashboard_anr';

function readStorage(key: string): string | undefined {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStorage(key: string, value: string | undefined): void {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore unavailable storage
  }
}

/**
 * Reads the parameters from the URL the app was opened with.
 * The login flow reloads the app without them, so a stored value is never cleared here.
 * sessionStorage is per tab, so a new window opened from Consolidate starts fresh.
 */
export function initUrlParameters(): void {
  const anr = new URLSearchParams(window.location.search).get('anr')?.trim();
  if (anr) {
    writeStorage(ANR_KEY, anr);
  }
}

export function getAnr(): string | undefined {
  return readStorage(ANR_KEY);
}

/** Router guard that keeps ?anr= in the URL when navigating between dashboards/views. */
export function keepUrlParameters(to: Route): Location | undefined {
  if (to.name === 'login' || to.name === 'login-callback') {
    return undefined;
  }

  const queryAnr = typeof to.query.anr === 'string' ? to.query.anr.trim() : '';
  if (queryAnr) {
    writeStorage(ANR_KEY, queryAnr);
    return undefined;
  }

  const anr = getAnr();
  if (!anr) {
    return undefined;
  }

  return {
    path: to.path,
    hash: to.hash,
    query: { ...to.query, anr },
    replace: true,
  };
}
